/**
 * Source-tool upload model.
 *
 * The board-upload flow is organised around the EDA tool the user
 * exported from (KiCad / EasyEDA) rather than a flat list of file
 * formats. Each tool declares a fixed set of file *slots*; the netlist
 * and BOM are compulsory, the schematic / PCB files optional. A single
 * upload posts all selected slots to `POST /projects/upload` in one
 * multipart request (the field names below match the backend exactly).
 *
 * Altium ingests as netlist + BOM only (Protel netlist carries no
 * geometry; native `.SchDoc`/`.PcbDoc` is a later phase).
 */

/** Tools we can actually ingest today. */
export type SourceTool = "kicad" | "easyeda" | "altium";

/**
 * Tool identifier. Kept as a distinct alias for places that may list
 * display-only / coming-soon tools alongside selectable ones; today it
 * matches `SourceTool` exactly.
 */
export type ToolId = SourceTool;

export type SlotKey =
  | "netlist"
  | "bom"
  | "kicad_sch"
  | "kicad_pcb"
  | "pcb_json";

export interface UploadSlot {
  key: SlotKey;
  /** Multipart field name expected by `POST /projects/upload`. */
  field: string;
  label: string;
  /** `accept` filter for the file picker (comma-separated extensions). */
  accept: string;
  required: boolean;
  hint: string;
  /** Size-cap bucket — BOMs are small, geometry files large. */
  cap: "netlist" | "bom";
}

export interface SourceToolDef {
  id: ToolId;
  label: string;
  /** Short helper rendered under the tool selector. */
  hint: string;
  disabled?: boolean;
  slots: UploadSlot[];
}

const NETLIST_SLOT_KICAD: UploadSlot = {
  key: "netlist",
  field: "netlist_file",
  label: "Netlist (.net)",
  accept: ".net",
  required: true,
  cap: "netlist",
  hint: "File → Export → Netlist → KiCad.",
};

const BOM_SLOT: UploadSlot = {
  key: "bom",
  field: "bom_file",
  label: "BOM (.csv)",
  accept: ".csv",
  required: true,
  cap: "bom",
  hint: "Required — reference designators + LCSC / MPN.",
};

const NETLIST_SLOT_ALTIUM: UploadSlot = {
  key: "netlist",
  field: "netlist_file",
  label: "Netlist (.net)",
  accept: ".net",
  required: true,
  cap: "netlist",
  hint: "Design → Netlist For Project → Protel.",
};

export const SOURCE_TOOLS: SourceToolDef[] = [
  {
    id: "kicad",
    label: "KiCad",
    hint: "Netlist + BOM are required; add the .kicad_sch / .kicad_pcb to paint the schematic & PCB tabs.",
    slots: [
      NETLIST_SLOT_KICAD,
      BOM_SLOT,
      {
        key: "kicad_sch",
        field: "kicad_sch",
        label: "Schematic (.kicad_sch) — optional",
        accept: ".kicad_sch",
        required: false,
        cap: "netlist",
        hint: "Optional — adds sheet hierarchy.",
      },
      {
        key: "kicad_pcb",
        field: "kicad_pcb",
        label: "PCB (.kicad_pcb) — optional",
        accept: ".kicad_pcb",
        required: false,
        cap: "netlist",
        hint: "Optional — paints the PCB tab from real coordinates.",
      },
    ],
  },
  {
    id: "easyeda",
    label: "EasyEDA",
    hint: "Netlist + BOM are required; add the PCB JSON for board geometry.",
    slots: [
      {
        ...NETLIST_SLOT_KICAD,
        hint: "Export → Netlist (bracket format).",
      },
      BOM_SLOT,
      {
        key: "pcb_json",
        field: "pcb_json",
        label: "PCB (.json) — optional",
        accept: ".json",
        required: false,
        cap: "netlist",
        hint: "Optional — export the PCB document as JSON.",
      },
    ],
  },
  {
    id: "altium",
    label: "Altium",
    hint: "Netlist + BOM. Export the netlist in Protel format; geometry (PCB/schematic) isn't ingested yet.",
    slots: [NETLIST_SLOT_ALTIUM, BOM_SLOT],
  },
];

/** Tools the user can actually pick (excludes disabled / display-only). */
export const SELECTABLE_TOOLS = SOURCE_TOOLS.filter((t) => !t.disabled);

export function getTool(id: SourceTool): SourceToolDef {
  const def = SOURCE_TOOLS.find((t) => t.id === id);
  if (!def) throw new Error(`Unknown source tool: ${id}`);
  return def;
}

/** Slot files keyed by slot key. */
export type SlotFiles = Partial<Record<SlotKey, File | null>>;

/** True when every required slot for `tool` has a file. */
export function requiredSlotsFilled(
  tool: SourceTool,
  files: SlotFiles,
): boolean {
  return getTool(tool)
    .slots.filter((s) => s.required)
    .every((s) => Boolean(files[s.key]));
}

/** True when at least one slot has a file. */
export function anySlotFilled(files: SlotFiles): boolean {
  return Object.values(files).some(Boolean);
}

export interface BoardUploadInit {
  tool: SourceTool;
  projectName: string;
  /** Target board name — omitted by the project-creation flow. */
  boardName?: string;
  /** Target board UUID. When set, the backend ingests onto this exact
   *  board (no name resolution), so a same-named board in another
   *  project can't be overwritten. Both upload dialogs know it. */
  boardId?: string;
  organisationId?: string | null;
  versionSemver?: string;
  versionDescription?: string;
  files: SlotFiles;
}

/**
 * Assemble the multipart body for `POST /projects/upload`. Only slots
 * declared by the chosen tool are appended, so a stray EasyEDA file can
 * never ride along on a KiCad upload (the backend 422s on a mismatch
 * regardless).
 */
export function buildBoardUploadFormData(init: BoardUploadInit): FormData {
  const fd = new FormData();
  fd.append("source_tool", init.tool);
  fd.append("project_name", init.projectName ?? "");
  if (init.boardName) fd.append("board_name", init.boardName);
  if (init.boardId) fd.append("board_id", init.boardId);
  if (init.organisationId) fd.append("organisation_id", init.organisationId);
  if (init.versionSemver?.trim()) {
    fd.append("version_semver", init.versionSemver.trim());
  }
  if (init.versionDescription?.trim()) {
    fd.append("version_description", init.versionDescription.trim());
  }
  for (const slot of getTool(init.tool).slots) {
    const file = init.files[slot.key];
    if (file) fd.append(slot.field, file, file.name);
  }
  return fd;
}
